
























































































































































































































































































































































































































































































































































































































































































import Vue from "vue";
import Component from "vue-class-component";
import { Inject, Prop, Ref } from "vue-property-decorator";
import { Getter, Action } from "vuex-class";
import { Route } from "vue-router";
import Cookies from "js-cookie";
import { RestClient } from "@elite/restclient";
import { IModelState, IValidator } from "@elite/validation";
import { ErrorLog } from "@elite/vue-logging";
import {
    AppGetterTypes,
    AppActionTypes,
    SetPromotion,
    AddTracking,
    PutAddress
} from "../store";
import { UpsellProductModals } from "../components";
import { Step1Route, ThankYouRoute } from "../router";
import {
    cookieKey,
    pushPageView,
    pushAddToCart,
    GtmPageType
} from "../tracking";
import {
    getFacebookCookieValue,
    getPaymentMethodCode
} from "../helpers";

import {
    IPromotionModel,
    ISelectListItem,
    IEmailInformation,
    ICheckoutModel,
    IAddressModel
} from "../models";
import {
    getPromotionRequestModel,
    getTrackingRequest
} from "../models/factories";
import { TranslateResult } from "vue-i18n";

@Component({ name: "CheckoutStep2" })
export default class CheckoutStep2 extends Vue {
    @Inject()
    restClient!: RestClient;

    @Inject()
    modelState!: IModelState;

    @Inject()
    errorLog!: ErrorLog;

    @Getter(AppGetterTypes.promotion)
    promotion!: IPromotionModel;

    @Getter(AppGetterTypes.checkout)
    checkout!: ICheckoutModel;

    @Getter(AppGetterTypes.paymentMethods)
    paymentMethods!: ISelectListItem[];

    @Getter(AppGetterTypes.genericOrderFailedMessage)
    genericOrderFailedMessage!: string;

    @Action(AppActionTypes.getPromotion)
    getPromotion!: SetPromotion;

    @Action(AppActionTypes.addTracking)
    addTracking!: AddTracking;

    @Action(AppActionTypes.putAddress)
    putAddress!: PutAddress;

    // eslint-disable-next-line no-undef
    assetBaseUrl: string = process.env.VUE_APP_CDN_URL + "/assets";

    // this is mapped from :slug in the route
    @Prop({ required: true })
    promotionSlug!: string;

    cvvModalOpen = false;
    orderIsSubmitting = false;

    maxExpirationDate = new Date();

    //Address Verification
    addressInvalid = false;
    validatedAddress: IAddressModel | null = null;
    showAddressValidationPopup = false;
    initialSubmit = true;

    addressTypes = {
        shipping: "shipping",
        billing: "billing"
    };

    hasUpsells(): boolean {
        return (
            !!this.promotion.upsellProducts &&
            this.promotion.upsellProducts.length > 0
        );
    }

    @Ref("upsells")
    upsellModals!: UpsellProductModals;

    @Ref("validationObserver")
    validationObserver!: IValidator;

    get localizedPaymentMethods(): ISelectListItem[] {
        const paymentMethods = this.paymentMethods;

        // Get localized names
        // ex:  common.credit-card
        //      common.debit-card
        const localize = (name: string): string =>
            this.$t(
                "common." + name.toLowerCase().replace(" ", "-")
            ).toString();

        paymentMethods.forEach(
            i => (i.text = i.text ? localize(i.text) : i.text)
        );

        return paymentMethods;
    }

    // The maxlength of this field changes if its an organizationSponsorship. The fluent logic for this does not convert to the
    // validation rules used by vue, so we add the maxlength property to handle client side validation and use fluent for the
    // server side validation.
    get firstNameMaxLength(): number {
        return this.promotion.isOrganizationSponsorship ? 40 : 125;
    }

    get validatedAddressIsDifferent(): boolean {
        if (!this.validatedAddress || !this.checkout) {
            return false;
        }

        return (
            this.validatedAddress.address !==
                this.checkout.shippingAddress?.address ||
            this.validatedAddress.address2 !==
                this.checkout.shippingAddress?.address2 ||
            this.validatedAddress.city !==
                this.checkout.shippingAddress?.city ||
            this.validatedAddress.state !==
                this.checkout.shippingAddress?.state ||
            this.validatedAddress.zipCode !==
                this.checkout.shippingAddress?.zipCode
        );
    }

    get validExpirationDate(): boolean {
        return (
            !this.checkout.expirationDate ||
            new Date(this.checkout.expirationDate) > this.maxExpirationDate
        );
    }

    async mounted(): Promise<void> {
        // Get email info from cookies
        const emailInfo: IEmailInformation | undefined = Cookies.getJSON(
            cookieKey.espiredEmail
        );

        // If promotion is not set, the user may have refreshed or skipped step 1
        // If we don't have an email, send them back to step-1
        if (!this.promotionSlug || !emailInfo || !emailInfo.email) {
            this.redirectToStep1();
            return;
        }

        if (!this.promotion) {
            // If the promotion doesn't exist, the user probably navigated directly to step-2
            const params = getPromotionRequestModel(
                this.$route.query,
                this.promotionSlug
            );

            await this.getPromotion(params);
        }

        this.setBackgroundAndTitle();

        // Set email information
        this.checkout.email = emailInfo.email;
        this.checkout.emailOptIn = !!emailInfo.emailOptIn;

        // Set sessionId that will be used as Order GUID
        this.checkout.sessionId = this.promotion.sessionId;

        if (this.checkout.promotionCode === "") {
            this.checkout.promotionCode = this.promotion.promotionCode;
        }

        // send tracking info
        if (this.promotion.sessionId) {
            this.addTracking(
                getTrackingRequest(
                    "checkout",
                    this.checkout,
                    this.promotion.sessionId,
                    window.location.href
                )
            );
        }

        // Push GTM events
        await pushPageView(GtmPageType.Cart);

        if (this.promotion.sessionId && this.promotion.promotionCode) {
            await pushAddToCart(
                this.promotion.sessionId,
                this.promotion.promotionCode
            );
        }
    }

    async validate(): Promise<boolean> {
        return (
            this.validExpirationDate &&
            (await this.validationObserver.validate())
        );
    }

    async processOrderClick(): Promise<void> {
        // If the order is already submitting, do not attempt to place another.
        if (this.orderIsSubmitting) {
            return;
        }

        if (!(await this.validate())) {
            this.orderIsSubmitting = false;
            return;
        }

        // Show upsells before order submission
        if (this.hasUpsells() && this.initialSubmit) {
            this.initialSubmit = false;
            await this.upsellModals.showUpsells();

            this.upsellModals.$once("finished", async () => {
                await this.processOrder();

                this.orderIsSubmitting = false;
            });
        } else {
            this.initialSubmit = false;
            await this.processOrder();

            this.orderIsSubmitting = false;
        }
    }

    async processOrder(): Promise<void> {
        const orderId = await this.submitOrder();

        if (!orderId) {
            return;
        }

        // Proceed with redirecting the user to the thank-you page.
        this.$router.push({
            name: ThankYouRoute.name,
            params: {
                slug: this.promotionSlug,
                orderId: orderId
            },
            query: this.$route.query // Pass all current query parameters to ThankYou page
        });
    }

    async submitOrder(): Promise<string | null> {
        await this.cleanseShippingAddress();

        this.orderIsSubmitting = true;

        if (this.addressInvalid) {
            return null;
        }

        this.checkout.expirationDateYear = Number(
            this.checkout.expirationDateYear
        );

        const paymentMethodCode = getPaymentMethodCode(
            this.checkout.cardNumber || ""
        );

        if (paymentMethodCode) {
            this.checkout.paymentMethodCode = paymentMethodCode;
        } else {
            this.modelState.addErrors(
                {
                    cardNumber: [
                        this.$t("step2.invalid-credit-card").toString()
                    ]
                },
                this
            );
            return null;
        }

        this.modelState.clear(this);

        // the last name field is required but we hide it when the promotion
        // is an organization sponsorship. We manually set it to "sponsorship"
        // here to pass validation.
        if (
            this.promotion.isOrganizationSponsorship &&
            this.checkout.shippingAddress
        ) {
            this.checkout.shippingAddress.lastName = "sponsorship";
        }

        // SET THE CURRENT URL ON THE CHECKOUT MODEL TO SEND TO THE API FOR TRACKING PURPOSES
        this.checkout.eventSourceUrl = window.location.href;
        this.checkout.facebookFbpCookieValue = getFacebookCookieValue("_fbp");
        this.checkout.facebookFbcCookieValue = getFacebookCookieValue("_fbc");

        try {
            // Place order
            const response = await this.restClient.postJson<string>(
                "/api/Checkout",
                this.checkout
            );

            if (!response) {
                return null;
            }

            if (this.modelState.hasErrors(response)) {
                this.modelState.addResponseErrors(response, this);
                // expiration date is special because it has month and year fields, but validated as a single DateTime
                // copy it from property errors to model errors so it shows in the validation summary
                if (this.modelState.propertyErrors.expirationDate) {
                    this.modelState.modelErrors.push(
                        this.modelState.propertyErrors.expirationDate[0]
                    );
                }

                return null;
            }

            return response.data || null;
        } catch (error) {
            this.modelState.modelErrors.push(this.genericOrderFailedMessage);

            this.errorLog.send(error as Error, this);

            return null;
        }
    }

    async cleanseShippingAddress(): Promise<void> {
        if (!this.checkout) {
            throw new Error();
        }

        const address: IAddressModel = {
            address: this.checkout.shippingAddress?.address || "",
            address2: this.checkout.shippingAddress?.address2 || "",
            city: this.checkout.shippingAddress?.city || "",
            state: this.checkout.shippingAddress?.state || "",
            zipCode: this.checkout.shippingAddress?.zipCode || ""
        };

        try {
            const validatedAddress = await this.putAddress(address);

            this.addressInvalid = false;
            this.validatedAddress = validatedAddress;

            // If the API returned null for the validated address, it is an indication that USPS is down. In this case, we should allow the user to continue without interruption.
            this.showAddressValidationPopup =
                !!this.validatedAddress && this.validatedAddressIsDifferent;
        } catch (e) {
            this.validatedAddress = null;

            // If the request failed (e.g. 500 Internal Server Error), Raygun will report the issue on our end and we should allow the user to continue.
            this.addressInvalid = false;
            this.showAddressValidationPopup = false;
        }

        if (!this.addressInvalid && this.showAddressValidationPopup) {
            await new Promise<void>(resolve => {
                this.$on("use-validated-address", () => {
                    this.$off("ignore-validated-address");

                    if (!this.validatedAddress) {
                        return;
                    }

                    this.checkout.shippingAddress =
                        this.checkout.shippingAddress || {};
                    this.checkout.shippingAddress.address =
                        this.validatedAddress.address || "";
                    this.checkout.shippingAddress.address2 =
                        this.validatedAddress.address2 || "";
                    this.checkout.shippingAddress.city =
                        this.validatedAddress.city || "";
                    this.checkout.shippingAddress.state =
                        this.validatedAddress.state || "";
                    this.checkout.shippingAddress.zipCode =
                        this.validatedAddress.zipCode || "";

                    resolve();
                });

                this.$on("ignore-validated-address", () => {
                    this.$off("use-validated-address");
                    resolve();
                });
            });
        }

        this.validatedAddress = null;
    }

    phoneBlur(key: string): void {
        if (!this.checkout) {
            return;
        }

        let phone;

        if (key === this.addressTypes.shipping) {
            phone = this.checkout.shippingAddress?.phone;
        }

        if (key === this.addressTypes.billing) {
            phone = this.checkout.billingAddress?.phone;
        }

        if (!phone) {
            return;
        }

        let replaced = phone.replace(/[^\d]/g, "");
        replaced = replaced.padEnd(10, "0");

        phone = `${replaced.substr(0, 3)}-${replaced.substr(
            3,
            3
        )}-${replaced.substr(6, 4)}`;

        if (key === this.addressTypes.shipping) {
            this.checkout.shippingAddress = this.checkout.shippingAddress || {};
            this.checkout.shippingAddress.phone = phone;
        }

        if (key === this.addressTypes.billing) {
            this.checkout.billingAddress = this.checkout.billingAddress || {};
            this.checkout.billingAddress.phone = phone;
        }
    }

    async processUpsells(upsellIds: number[]): Promise<void> {
        this.checkout.upsellIds = upsellIds;
    }

    setBackgroundAndTitle(): void {
        //Set background color
        if (this.promotion.backgroundColor) {
            document.body.style.backgroundColor = this.promotion.backgroundColor;
        }

        //Set Page Title
        if (this.promotion.name) {
            document.title = this.promotion.name;
        }
    }

    redirectToStep1(): Promise<Route> {
        return this.$router.push({
            name: Step1Route.name,
            params: { slug: this.promotionSlug },
            query: this.$route.query // Pass all current query parameters back to step 1
        });
    }

    // when the promotion is an organization sponsorship we need to change the
    // keys for some form fields.
    getLocalized(
        key: string,
        organizationSponsorshipKey: string
    ): TranslateResult {
        if (this.promotion && this.promotion.isOrganizationSponsorship) {
            return this.$t(organizationSponsorshipKey);
        }
        return this.$t(key);
    }
}
